import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Bell, X, ArrowRight } from "lucide-react";
import {
  canShowNative, wasPermissionAsked, requestNotificationPermission,
} from "../../lib/notify";
import { useChainStore } from "../../data/chainStore";

/* ═══════════════════════════════════════════════════════════════════
   ChainFlow OS — Notification Permission Prompt
   --------------------------------------------------------------------
   Calm operational ask. Surfaces ONLY when:
   • Notification API supported AND permission === "default"
   • The user hasn't been asked before in this browser
   • There is at least one deal that materially needs attention
     (chainHealth < 75 or stage = exchange/completion within 14d)
   • A short engagement delay has elapsed (we don't ask cold)

   Wording: "Get calm operational nudges when a transaction needs
   attention." (matches the user's spec — no marketing language.)
   ─────────────────────────────────────────────────────────────────── */

const SNOOZE_KEY = "cf:notif-prompt-snoozed_until";
const SHOW_DELAY_MS = 25_000;

const isSnoozed = () => {
  const until = parseInt(localStorage.getItem(SNOOZE_KEY) || "0", 10);
  return Number.isFinite(until) && until > Date.now();
};

const isStandalone = () =>
  (typeof window !== "undefined" && window.matchMedia("(display-mode: standalone)").matches) ||
  (typeof navigator !== "undefined" && navigator.standalone === true);

const shouldOffer = (deals = []) => {
  if (typeof window === "undefined") return false;
  if (!("Notification" in window)) return false;
  if (Notification.permission !== "default") return false;
  if (wasPermissionAsked()) return false;
  if (isSnoozed()) return false;

  // Only ask when there's a real operational reason
  const now = Date.now();
  const horizon = now + 14 * 86400000;
  const reasons = (deals || []).filter((d) => {
    if (d.archived) return false;
    if ((d.chainHealth ?? 90) < 75) return true;
    if (d.targetExchange) {
      const t = Date.parse(d.targetExchange);
      if (Number.isFinite(t) && t > now && t < horizon) return true;
    }
    return false;
  });
  return reasons.length > 0;
};

export const NotificationPrompt = () => {
  const { deals } = useChainStore();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [hidden, setHidden] = useState(false);

  const eligible = useMemo(() => shouldOffer(deals), [deals]);

  useEffect(() => {
    if (!eligible || hidden || isStandalone()) return undefined;
    const t = window.setTimeout(() => setOpen(true), SHOW_DELAY_MS);
    return () => window.clearTimeout(t);
  }, [eligible, hidden]);

  const enable = async () => {
    setBusy(true);
    const result = await requestNotificationPermission();
    setBusy(false);
    setOpen(false);
    setHidden(true);
    if (result === "granted") {
      // Subtle confirmation via in-app toast (already standard sonner behaviour)
    } else {
      // Snooze so we don't re-prompt cold
      try { localStorage.setItem(SNOOZE_KEY, String(Date.now() + 14 * 86400 * 1000)); } catch { /* noop */ }
    }
  };

  const snooze = () => {
    try { localStorage.setItem(SNOOZE_KEY, String(Date.now() + 7 * 86400 * 1000)); } catch { /* noop */ }
    setOpen(false);
    setHidden(true);
  };

  const dismissForever = () => {
    try {
      localStorage.setItem(SNOOZE_KEY, String(Date.now() + 365 * 86400 * 1000));
    } catch { /* noop */ }
    // Also mark as asked so we never come back
    try { localStorage.setItem("cf:notif-permission-asked-at", String(Date.now())); } catch { /* noop */ }
    setOpen(false);
    setHidden(true);
  };

  if (typeof document === "undefined") return null;
  if (!open || canShowNative()) return null;

  return createPortal(
    <div
      className="fixed left-1/2 z-[110] w-[min(420px,calc(100vw-24px))]"
      style={{
        bottom: "calc(env(safe-area-inset-bottom, 0px) + 84px)",
        transform: "translateX(-50%)",
      }}
    >
      <div
        role="dialog"
        aria-label="Enable operational notifications"
        data-testid="cf-notif-prompt"
        className="relative rounded-2xl overflow-hidden p-4 sm:p-5 cf-fade-up"
        style={{
          background:
            "linear-gradient(160deg, rgba(15,17,38,0.96) 0%, rgba(20,18,52,0.94) 55%, rgba(12,12,30,0.96) 100%)",
          border: "1px solid rgba(139,92,246,0.28)",
          boxShadow:
            "0 28px 60px -18px rgba(0,0,0,0.65), 0 0 0 1px rgba(139,92,246,0.12), 0 0 32px rgba(124,58,237,0.18)",
          backdropFilter: "blur(20px) saturate(160%)",
          WebkitBackdropFilter: "blur(20px) saturate(160%)",
        }}
      >
        <span
          aria-hidden="true"
          className="absolute -top-16 -right-12 w-44 h-44 rounded-full blur-3xl pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(139,92,246,0.40), transparent 70%)" }}
        />

        <div className="relative flex items-start gap-3">
          <span
            className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
            style={{
              background: "linear-gradient(135deg, #7C3AED 0%, #6D28D9 60%, #4F46E5 100%)",
              boxShadow:
                "0 14px 28px -10px rgba(124,58,237,0.65), inset 0 1px 0 rgba(255,255,255,0.22)",
            }}
            aria-hidden="true"
          >
            <Bell size={16} className="text-white" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-[10.5px] font-bold uppercase tracking-[0.18em] text-violet-300">
              Operational alerts
            </p>
            <h3 className="font-display text-[16px] font-bold text-white tracking-tight mt-1 leading-tight">
              Stay ahead of every transaction
            </h3>
            <p className="text-[12.5px] text-slate-300 mt-1 leading-snug">
              Get calm operational nudges when a transaction needs attention.
            </p>
          </div>
          <button
            type="button"
            onClick={snooze}
            aria-label="Not now"
            className="w-8 h-8 -mt-1 -mr-1 rounded-lg text-slate-400 hover:text-white hover:bg-white/[0.06] flex items-center justify-center shrink-0 transition-colors"
            data-testid="cf-notif-prompt-close"
          >
            <X size={14} />
          </button>
        </div>

        <div className="relative mt-4 flex items-center gap-2">
          <button
            type="button"
            onClick={enable}
            disabled={busy}
            data-testid="cf-notif-prompt-enable"
            className="flex-1 h-11 rounded-xl text-[13px] font-bold text-white inline-flex items-center justify-center gap-2 transition-transform hover:scale-[1.01] active:scale-[0.99]"
            style={{
              background:
                "linear-gradient(135deg, #7C3AED 0%, #6D28D9 50%, #5B21B6 100%)",
              boxShadow:
                "0 14px 28px -10px rgba(124,58,237,0.85), inset 0 1px 0 rgba(255,255,255,0.22), 0 0 0 1px rgba(139,92,246,0.30)",
              opacity: busy ? 0.7 : 1,
            }}
          >
            {busy ? "Enabling…" : "Enable alerts"}
            {!busy && <ArrowRight size={14} strokeWidth={2.4} />}
          </button>
          <button
            type="button"
            onClick={dismissForever}
            className="h-11 px-3 rounded-xl text-[12px] font-semibold text-slate-400 hover:text-slate-200 hover:bg-white/[0.04] transition-colors"
            data-testid="cf-notif-prompt-never"
          >
            Don't show again
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default NotificationPrompt;
